import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { RefundsApiAdapter } from '../adapters/RefundsApiAdapter';
import { Filters } from '../models/Filters';
import { RefundOrder } from '../models/RefundOrder';
import { Service } from '../services/Service';

export const useRefunds = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new RefundsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [previousOrders, setPreviousOrders] = useState<RefundOrder[]>([]);

  const { data, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isInitialLoading } =
    useInfiniteQuery({
      queryKey: ['refunds', filters],
      queryFn: ({ pageParam = 1 }) =>
        service.getPaginated({
          ...filters,
          page: pageParam,
        }),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 1,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      placeholderData: (previousData) => previousData,
    });

  const orders = useMemo(() => data?.pages.flatMap((page) => page.orders) ?? [], [data]);
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  useEffect(() => {
    if (orders.length > 0 && isFirstLoad) {
      setIsFirstLoad(false);
    }
  }, [orders.length, isFirstLoad]);

  useEffect(() => {
    if (orders.length > 0) {
      setPreviousOrders(orders);
    }
  }, [orders]);

  const displayOrders = isFetching && !isFirstLoad && previousOrders.length > 0 ? previousOrders : orders;

  return {
    orders: displayOrders,
    isLoading: isFirstLoad && isInitialLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    total: totalFromApi,
  };
};
